import React, { useEffect, useState, useMemo } from 'react'
import HeadingCard from 'components/Card/HeadingCard'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { staffEasebuzzListThunk, selectAllEasebuzzSubmerchants, selectEasebuzzListApi, staffEasebuzzDeleteThunk } from 'store/reducers/temple/easebuzzSubmerchantSlice'
import { unwrapResult } from '@reduxjs/toolkit'
import { Box, Link, Table, TableBody, TableCell, TableHead, TableRow, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress } from '@material-ui/core'
import EasebuzzSubmerchantForm from 'containers/Staff/EasebuzzSubmerchantForm'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import LockIcon from '@material-ui/icons/Lock'
import SyncIcon from '@material-ui/icons/Sync'
import ConfirmationDialog from '@crema/core/ConfirmationDialog'
import { staffEasebuzzCreateSubmerchantThunk, selectEasebuzzCreateSubmerchantApi, staffEasebuzzUpdateThunk, staffEasebuzzUpdateSubmerchantThunk } from 'store/reducers/temple/easebuzzSubmerchantSlice'
import { selectAllBankAccounts } from 'store/reducers/temple/bankAccountSlice'

type Props = {
    templeId?: string | number | null,
    bankAccountUrl?: string | null
}

const EasebuzzSubmerchantTable: React.FC<Props> = ({ templeId, bankAccountUrl }) => {
    const dispatch = useAppDispatch()
    const rows = useAppSelector(selectAllEasebuzzSubmerchants)
    const api = useAppSelector(selectEasebuzzListApi)
    const [open, setOpen] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [selectedUrl, setSelectedUrl] = useState<string | undefined>(undefined)

    const [editOpen, setEditOpen] = useState(false)
    const [editing, setEditing] = useState<any | undefined>(undefined)

    const openEdit = (row: any) => {
        setEditing(row)
        setEditOpen(true)
    }

    // password dialog states
    const [pwdDialogOpen, setPwdDialogOpen] = useState(false)
    const [selectedId, setSelectedId] = useState<string | number | undefined>(undefined)
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [passwordError, setPasswordError] = useState<string | null>(null)

    const createSubmerchantApi = useAppSelector(selectEasebuzzCreateSubmerchantApi)

    // per-row updating state for update-submerchant action
    const [updatingId, setUpdatingId] = useState<string | number | null>(null)

    const handleUpdateSubmerchant = async (id?: string | number) => {
        if (!id) return
        setUpdatingId(id)
        try {
            await dispatch(staffEasebuzzUpdateSubmerchantThunk({ id })).then(unwrapResult)
            // refresh list after update
            dispatch(staffEasebuzzListThunk(templeId ? { templeId } : {})).then(unwrapResult).catch(() => { })
        } catch (err) {
            // ignore errors for now
        } finally {
            setUpdatingId(null)
        }
    }

    const bankAccounts = useAppSelector(selectAllBankAccounts)
    const bankAccountsByUrl = useMemo(() => Object.fromEntries(bankAccounts.map(b => [b.url, b])), [bankAccounts])

    useEffect(() => {
        dispatch(staffEasebuzzListThunk(templeId ? { templeId } : {}))
            .then(unwrapResult)
            .catch(() => { })
    }, [dispatch, templeId])

    const handleDelete = async (url: string | undefined) => {
        if (!url) return
        try {
            await dispatch(staffEasebuzzDeleteThunk({ url })).then(unwrapResult)
        } catch (err) {
            // ignore
        }
    }

    const openConfirm = (url?: string) => {
        setSelectedUrl(url)
        setConfirmOpen(true)
    }

    const doDelete = async () => {
        if (!selectedUrl) return
        await handleDelete(selectedUrl)
        setConfirmOpen(false)
        setSelectedUrl(undefined)
    }

    const handlePwdSubmit = async () => {
        setPasswordError(null)
        if (!password || !confirmPassword) {
            setPasswordError('Both fields are required')
            return
        }
        if (password !== confirmPassword) {
            setPasswordError('Passwords do not match')
            return
        }
        if (!selectedId) {
            setPasswordError('No submerchant selected')
            return
        }

        try {
            await dispatch(staffEasebuzzCreateSubmerchantThunk({ id: selectedId, sub_merchant_password: password, sub_merchant_confirm_password: confirmPassword })).then(unwrapResult)
            // call update-submerchant endpoint to finalize/sync (best-effort)
            try {
                if (selectedId) {
                    await dispatch(staffEasebuzzUpdateSubmerchantThunk({ id: selectedId })).then(unwrapResult)
                }
            } catch (err) {
                // ignore update errors for now
            }
            // success
            setPwdDialogOpen(false)
            setSelectedId(undefined)
            setPassword('')
            setConfirmPassword('')
            setPasswordError(null)
            // refresh list
            dispatch(staffEasebuzzListThunk(templeId ? { templeId } : {})).then(unwrapResult).catch(() => { })
        } catch (err: any) {
            // API may return field errors
            if (err?.sub_merchant_password) setPasswordError(err.sub_merchant_password?.join?.('\n') || String(err.sub_merchant_password))
            else if (err?.detail) setPasswordError(String(err.detail))
            else setPasswordError('Failed to set password')
        }
    }

    return null

    return (
        <HeadingCard title="Easebuzz Submerchants" cardContent={false} hasheaderBackground={false}>
            <Box p={2}>
                <Box mb={2} display="flex" justifyContent="flex-end">
                    <Button variant="contained" color="primary" onClick={() => setOpen(true)}>Create</Button>
                </Box>
                {rows.length === 0 ? <p>{api.status === 'loading' ? 'Loading...' : 'No entries'}</p> : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Bank account</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.submerchantId ?? ''}</TableCell>
                                    <TableCell>{row.subMerchantName ?? ''}</TableCell>
                                    <TableCell>{row.subMerchantEmail ?? ''}</TableCell>
                                    <TableCell>{row.subMerchantPhone ?? ''}</TableCell>
                                    <TableCell>
                                        {(() => {
                                            const ba = bankAccountsByUrl[row.bankAccount ?? '']
                                            const label = ba ? (ba.bankName ? `${ba.bankName} — ${ba.accNumber}` : ba.accNumber) : row.bankAccount
                                            return (<Link href={row.bankAccount} target="_blank">{label}</Link>)
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center">
                                            <IconButton aria-label="Edit" title="Edit" size="small" onClick={() => openEdit(row)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton aria-label="Set password" title="Set password" size="small" onClick={() => { setSelectedId(row.id); setPassword(''); setConfirmPassword(''); setPasswordError(null); setPwdDialogOpen(true) }}>
                                                <LockIcon fontSize="small" />
                                            </IconButton>

                                            <IconButton aria-label="Update" title="Update submerchant" size="small" onClick={() => handleUpdateSubmerchant(row.id)} disabled={updatingId !== null && updatingId !== row.id}>
                                                {updatingId === row.id ? <CircularProgress size={18} /> : <SyncIcon fontSize="small" />}
                                            </IconButton>

                                            <IconButton aria-label="Delete" title="Delete" size="small" onClick={() => openConfirm(row.url as any)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>
            <EasebuzzSubmerchantForm open={open} onClose={() => setOpen(false)} templeId={templeId} bankAccountUrl={bankAccountUrl} />

            <EasebuzzSubmerchantForm
                open={editOpen}
                onClose={() => { setEditOpen(false); setEditing(undefined) }}
                templeId={templeId}
                bankAccountUrl={bankAccountUrl}
                initialData={editing}
                onSaved={async () => { setEditOpen(false); setEditing(undefined); dispatch(staffEasebuzzListThunk(templeId ? { templeId } : {})).then(unwrapResult).catch(() => { }) }}
            />

            <Dialog open={pwdDialogOpen} onClose={() => setPwdDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Set submerchant password</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Password"
                        type="password"
                        fullWidth
                        margin="dense"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <TextField
                        label="Confirm password"
                        type="password"
                        fullWidth
                        margin="dense"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    {passwordError ? <Box mt={1} color="error.main">{passwordError}</Box> : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPwdDialogOpen(false)}>Cancel</Button>
                    <Button color="primary" variant="contained" onClick={handlePwdSubmit} disabled={createSubmerchantApi.status === 'loading'}>
                        {createSubmerchantApi.status === 'loading' ? <CircularProgress size={18} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmationDialog
                open={confirmOpen}
                onDeny={() => setConfirmOpen(false)}
                onConfirm={doDelete}
                title={'Are you sure you want to delete this submerchant?'}
                dialogTitle={'Confirm delete'}
            />
        </HeadingCard>
    )
}

export default EasebuzzSubmerchantTable
